/**
 * Demo Sixes card generator.
 *
 * A fixture has 9 candidate buckets but a card uses only 7 (see {@link sixesMarketBucket}):
 *   - a random **flip** picks one team's Runs bucket + the *other* team's Player bucket;
 *   - Match and the other four 4-draw buckets are 4 cells each;
 *   - Team H2H and C8 Own (Player H2H) are "6-draw" buckets — 6 cells each.
 *
 * The settlement script showcases the v5 movement engine: four 4-draw buckets complete in order and
 * claim all four 4-draw lines — COL2 → COL3 → diagonal ↘ (DIAG_TLBR) → diagonal ↗ (DIAG_TRBL). The
 * component drives the slides through the shared {@link sixesMovement} engine, so the demo and real
 * cards move the same.
 *
 * Geometry follows the same **zone + rook** placement rules as real cards (`assignSixesGridPositions`):
 *  - all five 4-draw buckets are rook-placed in the outer rows (0,1,4,5) — one cell per outer row,
 *    every cell in a distinct column;
 *  - the two 6-draw buckets fill the middle-row side cells first, then the four leftover outer cells.
 *
 * On top of that it is **fixed and verified conflict-free** for the slide showcase: the completing
 * buckets are placed so no winner is ever bumped by another bucket's slide — only loser cells get
 * displaced. (Most rook layouts are *not* conflict-free for a four-line claim, so the geometry is a
 * specific verified one — see `sixesDemoLayout.test.js`.) Bucket identities + labels still vary by
 * flip and shuffle, and the structure (which cell is which role) is fixed so every variant stays
 * rook + zone + conflict-free.
 */
import { SIXES_MARKET_CELL_COUNT } from "../lib/sixesGridLayout";
import type { SixesBucket } from "../lib/sixesMarketBucket";
import { seedFromCardId, seededRng, type SixesRng } from "../lib/sixesMovement";

/** Label pools per bucket (demo copy). Pools are >= the cells the bucket can take. */
const BUCKET_LABEL_POOLS: Record<SixesBucket, readonly string[]> = {
    team_h2h: ["TEAM A V B", "MOST SIXES", "MOST FOURS", "TOSS WINNER", "MATCH WINNER", "HIGH 1ST OVER"],
    firsts: ["FIRST BALL RUNS", "FIRST OVER RUNS", "FIRST WICKET", "FIRST 6 OVERS"],
    team_a_runs: ["TEAM A OVER 1", "TEAM A OVER 6", "TEAM A OVER 15", "TEAM A FIRST DELIVERY"],
    team_b_runs: ["TEAM B OVER 1", "TEAM B OVER 6", "TEAM B OVER 15", "TEAM B FIRST DELIVERY"],
    innings: ["TEAM A INN RUNS", "TEAM B INN RUNS", "TEAM A INN SIXES", "TEAM B INN SIXES"],
    team_a_player: ["IYER RUNS", "SHASHANK SIXES", "STOINIS RUNS", "INGLIS FOURS"],
    team_b_player: ["ROHIT RUNS", "SURYA SIXES", "TILAK RUNS", "HARDIK FOURS"],
    match: ["MATCH RUNS", "MATCH SIXES", "MATCH FOURS", "MATCH WICKETS", "MATCH HIGHEST SCORE", "MATCH 50+ RUNS"],
    c8_own: ["IYER V ROHIT", "SURYA V SHASHANK", "TILAK V STOINIS", "HARDIK V INGLIS", "ISHAN V PRABH", "MAXWELL V NAMAN"],
};

// ---- Fixed, rook + zone, conflict-free geometry (market-cell indices) -------
// Four completing 4-draw buckets, in claim order. All four (plus the non-completing bucket) are
// rook-placed in the outer rows (one cell per outer row, distinct columns). Verified so each visibly
// slides onto its line and no winner is ever bumped by another bucket's slide — only losers get
// displaced (`sixesDemoLayout.test.js` re-checks this against the live movement engine).
const COMPLETING_FOUR_CELLS = [
    [2, 9, 25, 26], // role 0 → COL2
    [1, 6, 24, 29], // role 1 → COL3
    [0, 11, 21, 30], // role 2 → DIAG_TLBR (↘)
    [5, 10, 20, 27], // role 3 → DIAG_TRBL (↗)
] as const;
// The 5th (non-completing) 4-draw bucket — losers only, also rook-placed in the outer rows.
const NON_COMPLETING_FOUR_CELLS = [4, 8, 23, 31] as const;
// Both 6-draw buckets are non-completing here (losers): the 12 cells are the 8 middle-row side cells
// plus the 4 leftover outer cells (zone rule). The split is cosmetic — neither ever slides.
const SIX_A_CELLS = [3, 12, 13, 16, 17, 22] as const;
const SIX_B_CELLS = [7, 14, 15, 18, 19, 28] as const;

/** A completing bucket the sim slides — the engine derives which line it claims (by completion order). */
export type SixesSlideTarget = {
    id: string;
    /** Fires when this bucket crosses its slide threshold. */
    bucket: SixesBucket;
};

export type GeneratedSixesDemoCard = {
    flip: "A" | "B";
    /** Per market-cell index (0..31): which bucket it belongs to. */
    buckets: (SixesBucket | null)[];
    /** Per market-cell index: the short grid label. */
    labels: string[];
    /** Cells (by index) that settle as a win in the sim. */
    winningIndices: number[];
    /** Completing buckets, in completion order — each slides onto its engine-claimed line. */
    slideTargets: readonly SixesSlideTarget[];
    /** Resolve order for the sim — wins and misses interleaved, wins kept in bucket-claim order. */
    resolutionOrder: number[];
    /** Coin payout per win, in resolution order. */
    coinPayouts: number[];
};

function shuffle<T>(items: readonly T[], rng: SixesRng): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        [out[i], out[j]] = [out[j]!, out[i]!];
    }
    return out;
}

/**
 * Randomly merge two sequences while preserving each one's internal order. Each step picks from a
 * stream with probability proportional to its remaining count, which self-balances the two so they
 * stay interleaved end to end (no long run of one kind). Used to sprinkle the demo's misses through
 * its wins instead of resolving every miss first.
 */
function interleavePreservingOrder<T>(a: readonly T[], b: readonly T[], rng: SixesRng): T[] {
    const out: T[] = [];
    let i = 0;
    let j = 0;
    while (i < a.length || j < b.length) {
        const remA = a.length - i;
        const remB = b.length - j;
        const takeA = j >= b.length || (i < a.length && rng() * (remA + remB) < remA);
        if (takeA) out.push(a[i++]!);
        else out.push(b[j++]!);
    }
    return out;
}

const COIN_PAYOUT_SCALE = [55, 50, 48, 45, 42, 40, 38, 36, 35, 34, 32, 30, 28, 27, 25, 24, 22, 20] as const;

/**
 * Build a demo card — buckets, labels, and the settlement script.
 * Pass a seed (e.g. match id) for a stable board across reloads; omit for a fresh random card.
 */
export function generateSixesDemoCard(seed?: string | number): GeneratedSixesDemoCard {
    const rng = seed != null ? seededRng(seedFromCardId(seed)) : Math.random;
    const flip: "A" | "B" = rng() < 0.5 ? "A" : "B";
    // State A: Team A Runs + Team B Player · State B: Team B Runs + Team A Player.
    const runsBucket: SixesBucket = flip === "A" ? "team_a_runs" : "team_b_runs";
    const playerBucket: SixesBucket = flip === "A" ? "team_b_player" : "team_a_player";

    // Shuffle which bucket plays which fixed role: first 4 complete (COL2/COL3/DIAG↘/DIAG↗), last
    // one doesn't. The engine claims lines by completion order, so role order = claim order.
    const fourDrawBuckets = shuffle<SixesBucket>(
        ["firsts", "innings", "match", runsBucket, playerBucket],
        rng,
    );
    const completingFour = fourDrawBuckets.slice(0, 4);
    const nonCompletingFour = fourDrawBuckets[4]!;
    const [sixA, sixB] = shuffle<SixesBucket>(["team_h2h", "c8_own"], rng);

    const buckets: (SixesBucket | null)[] = Array.from({ length: SIXES_MARKET_CELL_COUNT }, () => null);
    const place = (bucket: SixesBucket, cells: readonly number[]) => {
        for (const idx of cells) buckets[idx] = bucket;
    };
    completingFour.forEach((bucket, role) => place(bucket, COMPLETING_FOUR_CELLS[role]!));
    place(nonCompletingFour, NON_COMPLETING_FOUR_CELLS);
    place(sixA!, SIX_A_CELLS);
    place(sixB!, SIX_B_CELLS);

    // Labels — draw from each bucket's pool without repeats.
    const poolCursors: Partial<Record<SixesBucket, number>> = {};
    const labels = buckets.map((bucket) => {
        if (!bucket) return "";
        const pool = BUCKET_LABEL_POOLS[bucket];
        const cursor = poolCursors[bucket] ?? 0;
        poolCursors[bucket] = cursor + 1;
        return pool[cursor % pool.length] ?? "";
    });

    // ---- Settlement script -------------------------------------------------
    // The four completing 4-draw buckets finish one-after-another, so the engine claims lines in
    // completion order: COL2 → COL3 → diagonal ↘ → diagonal ↗. Only completing cells win — every
    // slide bumps losers only.
    const fourCells = completingFour.map((_, role) => [...COMPLETING_FOUR_CELLS[role]!]);
    const winningIndices = fourCells.flat();

    const slideTargets: SixesSlideTarget[] = completingFour.map((bucket, i) => ({
        id: `slide-four-${i}`,
        bucket,
    }));

    // Wins stay in bucket-completion order (bucket 0's four, then 1's, …) so the engine still claims
    // COL2 → COL3 → ↘ → ↗ in that order — the verified conflict-free sequence; only the order WITHIN
    // each bucket is shuffled.
    const winsInOrder = [
        ...shuffle(fourCells[0]!, rng),
        ...shuffle(fourCells[1]!, rng),
        ...shuffle(fourCells[2]!, rng),
        ...shuffle(fourCells[3]!, rng),
    ];
    // Misses are interleaved through the wins so the board fills with a mix of correct + wrong cells
    // the whole way through (rather than every miss first, then a burst of wins + slides). Each
    // bucket still completes — and slides — as soon as its 4th win lands, now spread across the run.
    const completingCells = new Set<number>(winningIndices);
    const misses = shuffle(
        Array.from({ length: SIXES_MARKET_CELL_COUNT }, (_, i) => i).filter((i) => !completingCells.has(i)),
        rng,
    );
    const resolutionOrder = interleavePreservingOrder(winsInOrder, misses, rng);

    const coinPayouts = winningIndices.map((_, i) => COIN_PAYOUT_SCALE[i] ?? 20);

    return { flip, buckets, labels, winningIndices, slideTargets, resolutionOrder, coinPayouts };
}
